import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { OrganizationPublic } from "@dugads/shared";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Users, Plus, Pencil, Trash2 } from "lucide-react";
import { usePinFilters } from "@/lib/pins-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { PinRowButton } from "@/components/pin-row-button";

export default function OrganizationsPage() {
  const [selected, setSelected] = useState<OrganizationPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const { activeOrgIds } = usePinFilters();

  const pinKey = activeOrgIds.join(",");

  async function handleDelete(orgId: string) {
    if (!confirm("Are you sure you want to delete this organization? This cannot be undone.")) return;
    try {
      await api.deleteOrganization(orgId);
      if (selected?.id === orgId) setSelected(null);
      setRefreshTick((t) => t + 1); // bump fetchKey -> DataTable refetches
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete organization");
    }
  }

  // Pin filtering is a page-local concern the /organizations endpoint does not
  // model, so when pins are active we fetch the (small) full set and apply
  // search/sort/pagination + the pin filter client-side. With no pins, the
  // server does the work directly.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: OrganizationPublic[]; total: number }> => {
      if (activeOrgIds.length > 0) {
        const res = await api.getOrganizations();
        setCanCreate(res.organizations.some((o) => o._actions.includes("delete")));
        const scoped = res.organizations.filter((o) => activeOrgIds.includes(o.id));
        return applyClientListParams(scoped, params, {
          searchFields: (o) => [o.name, o.slug, o.type],
          sortAccessors: {
            name: (o) => o.name,
            type: (o) => o.type,
            created_at: (o) => o.created_at,
          },
          defaultSort: "name",
        });
      }
      const res = await api.getOrganizations({
        page: params.page,
        per_page: params.per_page,
        search: params.search,
        sort: params.sort,
        order: params.order,
      });
      setCanCreate(res.organizations.some((o) => o._actions.includes("delete")));
      return { data: res.organizations, total: res.total ?? res.organizations.length };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [pinKey],
  );

  const columns: DataTableColumn<OrganizationPublic>[] = [
    { key: "name", header: "Name", sortable: true, className: "font-medium", render: (o) => o.name },
    { key: "type", header: "Type", sortable: true, className: "capitalize", render: (o) => o.type },
    {
      key: "created_at", header: "Created", sortable: true, className: "text-muted-foreground",
      render: (o) => new Date(o.created_at).toLocaleDateString(),
    },
    {
      key: "actions", header: "", headerClassName: "w-32",
      render: (org) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <PinRowButton pinType="organization" entityType="Organization" entityId={org.id} />
          <Link to={`/organizations/${org.slug}/users`} className={buttonVariants({ variant: "ghost", size: "sm" })} title="Users" data-action="users">
            <Users className="h-4 w-4" />
          </Link>
          {org._actions.includes("edit") && (
            <Link to={`/organizations/${org.slug}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="edit">
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {org._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(org.id)} data-action="delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="organizations-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Organizations</h1>
            <p className="text-muted-foreground">Platform organizations</p>
          </div>
          <div className="flex gap-2">
            {canCreate && (
              <Link to="/organizations/new" className={buttonVariants({ size: "sm" })} data-action="create">
                <Plus className="mr-2 h-4 w-4" />
                Add Organization
              </Link>
            )}
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        <PinFilterBar entityType="Organization" />

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={`${pinKey}:${refreshTick}`}
            rowKey={(o) => o.id}
            rowProps={(o) => ({ "data-item": "organization", "data-item-id": o.id })}
            searchPlaceholder="Search organizations..."
            defaultSort="name"
            emptyMessage="No organizations found."
            onRowClick={(o) => setSelected(o)}
            rowClassName={(o) => (selected?.id === o.id ? "bg-muted" : undefined)}
            onStateChange={setTableState}
          />
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Organization">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Slug">{selected.slug}</PreviewField>
            <PreviewField label="Type"><span className="capitalize">{selected.type}</span></PreviewField>
            <PreviewField label="Created">{new Date(selected.created_at).toLocaleDateString()}</PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
